'use client';

import type { Permission } from '@up2stay/up2promo-api-client';
import type { WorkspaceRoleListItemDto } from '@up2stay/up2promo-api-client';
import {
  Permission as PermissionEnum,
  type WorkspaceMemberRole,
} from '@up2stay/up2promo-api-client';
import { Button } from '@up2stay/up2promo-ui';
import { Input } from '@up2stay/up2promo-ui';
import { Label } from '@up2stay/up2promo-ui';
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@up2stay/up2promo-ui';
import { Textarea } from '@up2stay/up2promo-ui';
import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { mapRoleApiError } from '@/core/authz/map-role-api-error';
import {
  resolveRoleListItemDescription,
  resolveRoleListItemName,
} from '@/core/authz/role-assignment';
import {
  SettingsGroup,
  SettingsGroupFooter,
  SettingsGroupLabel,
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { SettingsInfoHint } from '@/core/components/settings/settings-info-hint';
import {
  createPermissionLabels,
  PermissionMatrix,
} from '@/core/components/workspace/roles/permission-matrix';
import { TEAM_COLOR_PRESETS } from '@/core/components/workspace/teams/team-color-presets';
import { useMyMemberships } from '@/core/hooks/use-my-memberships';
import { usePermissions } from '@/core/hooks/use-permissions';
import { useCreateRole, useUpdateRole } from '@/core/hooks/use-workspace-roles';
import { triggerTapHaptic } from '@/core/lib/dnd-haptics';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import { getApiValidationErrorCode } from '@/core/utils/api-error.utils';

export type RoleEditorDict = {
  createTitle: string;
  editTitle: string;
  viewTitle: string;
  description: string;
  viewDescription: string;
  nameLabel: string;
  namePlaceholder: string;
  descriptionLabel: string;
  descriptionPlaceholder: string;
  colorLabel: string;
  permissionsLabel: string;
  permissionsReadOnlyHint: string;
  permissionAlwaysGranted: string;
  searchPermissions: string;
  save: string;
  saving: string;
  cancel: string;
  close: string;
  createSuccess: string;
  updateSuccess: string;
  selfLockoutWarning: string;
  errors: {
    generic: string;
    roleNameAlreadyExists: string;
    invalidRoleName: string;
    cannotModifySystemRole: string;
    cannotDeleteSystemRole: string;
    roleInUse: string;
    permissionEscalation: string;
    forbiddenPermissions: string;
    invalidPermissions: string;
    entitlementRequired: string;
  };
};

interface RoleEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  role?: WorkspaceRoleListItemDto | null;
  dict: RoleEditorDict;
  permissionsDict: Record<string, unknown>;
  builtinRoleLabels: Record<WorkspaceMemberRole, string>;
  builtinRoleDescriptions?: Partial<Record<WorkspaceMemberRole, string>>;
  onSuccess: () => void;
  canEditCustomRoles: boolean;
}

export function RoleEditor({
  open,
  onOpenChange,
  workspaceId,
  role,
  dict,
  permissionsDict,
  builtinRoleLabels,
  builtinRoleDescriptions,
  onSuccess,
  canEditCustomRoles,
}: RoleEditorProps) {
  const isSystem = role?.isSystem ?? false;
  const isCreate = !role;
  const readOnly = isSystem || (!isCreate && !canEditCustomRoles);

  const { can } = usePermissions();
  const currentWorkspace = useCurrentWorkspace();
  const { memberships } = useMyMemberships();
  const createRole = useCreateRole(workspaceId);
  const updateRole = useUpdateRole(workspaceId);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('');
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [seededFormKey, setSeededFormKey] = useState<string | null>(null);
  // Name errors sit at the field, which is far above the save button (DEV-821).
  const [nameError, setNameError] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const showNameError = (message: string) => {
    setNameError(message);
    nameInputRef.current?.scrollIntoView({ block: 'center' });
    nameInputRef.current?.focus({ preventScroll: true });
  };

  const formSeedKey = open ? (role?.id ?? '__create__') : null;
  if (formSeedKey && formSeedKey !== seededFormKey) {
    setSeededFormKey(formSeedKey);
    setName(role ? resolveRoleListItemName(role, builtinRoleLabels) : '');
    setDescription(
      role
        ? (resolveRoleListItemDescription(
            role,
            builtinRoleDescriptions ?? {},
          ) ?? '')
        : '',
    );
    setColor(role?.color ?? '');
    setPermissions((role?.permissions as Permission[]) ?? []);
    setNameError(null);
  } else if (!formSeedKey && seededFormKey !== null) {
    setSeededFormKey(null);
  }

  const permissionLabels = useMemo(
    () => createPermissionLabels(permissionsDict),
    [permissionsDict],
  );

  const title = isCreate
    ? dict.createTitle
    : readOnly
      ? dict.viewTitle
      : dict.editTitle;

  const isSubmitting = createRole.isPending || updateRole.isPending;

  const isEditingOwnCustomRole = useMemo(() => {
    if (!role || role.isSystem || !currentWorkspace) return false;
    const membership = memberships.find(
      (m) => m.workspace?.id === currentWorkspace.id,
    );
    const customRoleId = membership?.customRoleId?.trim();
    return !!customRoleId && customRoleId === role.id;
  }, [role, currentWorkspace, memberships]);

  const hasSelfLockoutRisk = useMemo(() => {
    if (readOnly || isCreate || !isEditingOwnCustomRole) return false;
    const hadRolesManage = (role?.permissions ?? []).includes(
      PermissionEnum.ROLES_MANAGE,
    );
    const keepsRolesManage = permissions.includes(PermissionEnum.ROLES_MANAGE);
    return hadRolesManage && !keepsRolesManage;
  }, [
    readOnly,
    isCreate,
    isEditingOwnCustomRole,
    role?.permissions,
    permissions,
  ]);

  const handleSubmit = async () => {
    if (readOnly) {
      onOpenChange(false);
      return;
    }

    if (hasSelfLockoutRisk) {
      toast.error(dict.selfLockoutWarning);
      return;
    }

    const trimmedName = name.trim();
    if (!trimmedName) {
      showNameError(dict.errors.invalidRoleName);
      return;
    }

    try {
      if (isCreate) {
        await createRole.mutateAsync({
          name: trimmedName,
          description: description.trim() || undefined,
          permissions,
          color: color.trim() || undefined,
        });
        toast.success(dict.createSuccess);
      } else if (role) {
        await updateRole.mutateAsync({
          roleId: role.id,
          body: {
            name: trimmedName,
            description: description.trim(),
            permissions,
            color: color.trim() || undefined,
          },
        });
        toast.success(dict.updateSuccess);
      }
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error('Failed to save role:', error);
      const message = mapRoleApiError(error, dict.errors);
      if (getApiValidationErrorCode(error, 'name')) {
        showNameError(message);
        return;
      }
      toast.error(message);
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(next) => {
        void triggerTapHaptic();
        onOpenChange(next);
      }}
    >
      <ResponsiveDialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>{title}</ResponsiveDialogTitle>
          <ResponsiveDialogDescription className="sr-only">
            {readOnly ? dict.viewDescription : dict.description}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}
          className="flex flex-col gap-4 py-1"
        >
          <SettingsGroup>
            <SettingsGroupRows>
              <SettingsGroupRow>
                <div className="space-y-2">
                  <Label htmlFor="role-name">{dict.nameLabel}</Label>
                  <Input
                    ref={nameInputRef}
                    id="role-name"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      if (nameError) setNameError(null);
                    }}
                    placeholder={dict.namePlaceholder}
                    disabled={readOnly || isSubmitting}
                    maxLength={80}
                    aria-invalid={nameError ? true : undefined}
                    aria-describedby={nameError ? 'role-name-error' : undefined}
                  />
                  {nameError ? (
                    <p
                      id="role-name-error"
                      className="text-xs text-destructive"
                      role="alert"
                    >
                      {nameError}
                    </p>
                  ) : null}
                </div>
              </SettingsGroupRow>

              <SettingsGroupRow separated>
                <div className="space-y-2">
                  <Label htmlFor="role-description">
                    {dict.descriptionLabel}
                  </Label>
                  <Textarea
                    id="role-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={dict.descriptionPlaceholder}
                    disabled={readOnly || isSubmitting}
                    maxLength={500}
                    rows={2}
                  />
                </div>
              </SettingsGroupRow>

              {!isSystem ? (
                <SettingsGroupRow separated>
                  <div className="space-y-2">
                    <Label>{dict.colorLabel}</Label>
                    <div className="flex flex-wrap gap-2">
                      {TEAM_COLOR_PRESETS.map((preset) => {
                        const isActive = color === preset;
                        return (
                          <button
                            key={preset}
                            type="button"
                            aria-pressed={isActive}
                            onClick={() => setColor(preset)}
                            disabled={readOnly || isSubmitting}
                            className={`h-8 w-8 rounded-full border-2 transition ${
                              isActive
                                ? 'scale-110 border-foreground'
                                : 'border-transparent hover:scale-105'
                            }`}
                            style={{ backgroundColor: preset }}
                          />
                        );
                      })}
                      <button
                        type="button"
                        aria-pressed={!color}
                        onClick={() => setColor('')}
                        disabled={readOnly || isSubmitting}
                        className={`h-8 rounded-full border px-2 text-xs ${
                          !color
                            ? 'border-foreground bg-muted'
                            : 'border-input bg-background'
                        }`}
                      >
                        —
                      </button>
                    </div>
                  </div>
                </SettingsGroupRow>
              ) : null}
            </SettingsGroupRows>
          </SettingsGroup>

          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-0.5">
              <SettingsGroupLabel>{dict.permissionsLabel}</SettingsGroupLabel>
              {readOnly ? (
                <SettingsInfoHint label={dict.permissionsLabel}>
                  {dict.permissionsReadOnlyHint}
                </SettingsInfoHint>
              ) : null}
            </div>
            <PermissionMatrix
              value={permissions}
              onChange={setPermissions}
              canAssign={(permission) => can(permission)}
              labels={permissionLabels}
              readOnly={readOnly}
              searchPlaceholder={dict.searchPermissions}
              alwaysGrantedLabel={dict.permissionAlwaysGranted}
            />
            {!readOnly ? (
              <SettingsGroupFooter>{dict.description}</SettingsGroupFooter>
            ) : null}
          </div>

          {hasSelfLockoutRisk ? (
            <p className="text-sm text-amber-600 dark:text-amber-400">
              {dict.selfLockoutWarning}
            </p>
          ) : null}

          {/* Stays in view below the long permission list (DEV-821). */}
          <ResponsiveDialogFooter className="sticky bottom-0 z-10 border-t border-border/60 bg-background py-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {readOnly ? dict.close : dict.cancel}
            </Button>
            {!readOnly ? (
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? dict.saving : dict.save}
              </Button>
            ) : null}
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
